import { faQuestionCircle } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Alert, Box, CircularProgress, Container, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FaqItem, faqApi } from '../api/faq'
import FaqList from '../components/faq/FaqList'

export default function FaqPage() {
    const { t } = useTranslation()
    const [faqs, setFaqs] = useState<FaqItem[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    // Charger les FAQs
    const loadFaqs = async () => {
        try {
            setLoading(true)
            // Récupérer uniquement les FAQs actives
            const data = await faqApi.getAll(undefined, true)
            // Filtrer à nouveau côté client pour s'assurer qu'aucune FAQ inactive n'est affichée
            const activeFaqs = data.filter((faq) => faq.isActive !== false)
            setFaqs(activeFaqs)
            setError(null)
        } catch (err) {
            console.error('Error loading FAQs:', err)
            setError(t('common.errorOccurred'))
        } finally {
            setLoading(false)
        }
    }

    // Charger les FAQs au chargement de la page et quand les filtres changent
    useEffect(() => {
        loadFaqs()
    }, [])

    const renderFaqContent = () => {
        if (loading) {
            return (
                <Box display="flex" justifyContent="center" p={4}>
                    <CircularProgress />
                </Box>
            )
        }

        if (faqs.length > 0) {
            return <FaqList faqs={faqs} />
        }

        return (
            <Box p={4} textAlign="center">
                <Typography variant="body1">{t('faq.noFaq')}</Typography>
            </Box>
        )
    }

    return (
        <Container maxWidth="md" sx={{ py: { xs: 3, md: 6 } }}>
            <Box
                textAlign="center"
                mb={6}
                sx={{
                    position: 'relative',
                    '&::after': {
                        content: '""',
                        display: 'block',
                        width: '80px',
                        height: '4px',
                        backgroundColor: 'var(--primary-color, #B40000)',
                        margin: '16px auto 0',
                        borderRadius: '2px',
                    },
                }}
            >
                <FontAwesomeIcon
                    icon={faQuestionCircle}
                    size="2x"
                    style={{
                        color: 'var(--primary-color, #B40000)',
                        marginBottom: '16px',
                    }}
                />
                <Typography
                    variant="h3"
                    component="h1"
                    sx={{
                        fontWeight: 700,
                        fontSize: { xs: '2.4rem', md: '3rem' },
                        mb: 2,
                        color: 'var(--primary-color, #B40000)',
                    }}
                >
                    {t('faq.title')}
                </Typography>
            </Box>

            {error && (
                <Alert
                    severity="error"
                    sx={{
                        mb: 4,
                        borderRadius: 2,
                        boxShadow: '0 4px 20px 0 rgba(0,0,0,0.05)',
                    }}
                >
                    {error}
                </Alert>
            )}

            <Box
                sx={{
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    boxShadow: '0 8px 30px rgba(0,0,0,0.06)',
                    overflow: 'hidden',
                }}
            >
                {renderFaqContent()}
            </Box>
        </Container>
    )
}
